import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Tbody,
  Tr,
  Td,
  Text,
  Skeleton,
  Button,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import { getModificationAction } from '../../../modules/catalog/actions';
import {
  selectModificationsList,
  selectIsModificationLoading,
} from '../../../modules/catalog/selectors';
import { ModificationDTO, ParameterDTO } from '../../../types';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../constants/routes';

export const ModificationsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { type, mark, model } = useParams();

  const modificationsList = useAppSelector(selectModificationsList);
  const loading = useAppSelector(selectIsModificationLoading);
  const itemsCount = modificationsList?.itemsCount;
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const modifications = modificationsList?.modifications || [];

  useEffect(() => {
    dispatch(getModificationAction.request({ type, mark, model, page: currentPage }));
  }, [currentPage, dispatch]);

  useEffect(() => {
    if (itemsCount && +itemsCount < currentPage * 20) {
      setHasMore(false);
    }
  }, [itemsCount, currentPage, loading]);

  const handleLoadMore = () => {
    setCurrentPage(prevPage => prevPage + 1);
  };

  const handleSelectModification = (value: string) => {
    navigate(
      generatePath(routes.CATALOG_GROUPS, {
        type,
        mark,
        model,
        modification: value,
      }),
    );
  };

  const getParameterValue = (parameters: ParameterDTO[], key: string) => {
    const param = parameters.find(p => p.key === key);
    return param ? param.value : '—';
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box mt={6} p={6}>
      <Flex alignItems="center" onClick={handleBack} cursor="pointer">
        <IconButton aria-label="Back" icon={<ChevronLeftIcon boxSize="32px" />} variant="ghost" />
        <Heading size="lg">
          Модификации {mark} {modificationsList?.model.name}
        </Heading>
      </Flex>

      <Text mb={4} mt={2} ml={10} color="gray.500">
        {`Выберите ${modifications.length === 0 ? 'другой' : 'нужный'} вариант`}
      </Text>

      {loading && (
        <Table variant="simple">
          <Tbody>
            {Array.from({ length: 10 }).map((_, index) => (
              <Tr key={index}>
                <Td>
                  <Skeleton height="20px" />
                </Td>
                <Td>
                  <Skeleton height="20px" />
                </Td>
                <Td>
                  <Skeleton height="20px" />
                </Td>
                <Td>
                  <Skeleton height="20px" />
                </Td>
                <Td>
                  <Skeleton height="20px" />
                </Td>
                <Td>
                  <Skeleton height="20px" />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {!loading && modifications.length === 0 && (
        <Flex mt={4} textAlign="center" height="300px" alignItems="center" justifyContent="center">
          <Text color="gray.500">Нет данных для отображения</Text>
        </Flex>
      )}

      {!loading && modifications.length > 0 && (
        <Table variant="simple">
          <Tbody>
            <Tr backgroundColor="hsl(0deg 0% 0% / 8%)">
              <Td fontWeight="bold">Название</Td>
              <Td fontWeight="bold">Год</Td>
              <Td fontWeight="bold">Двигатель</Td>
              <Td fontWeight="bold">Трансмиссия</Td>
              <Td fontWeight="bold">Регион</Td>
              <Td fontWeight="bold">Описание</Td>
            </Tr>
            {modifications.map((modification: ModificationDTO) => (
              <Tr
                key={modification.id}
                onClick={() => handleSelectModification(modification.id)}
                _hover={{ bg: 'gray.100', cursor: 'pointer' }}
              >
                <Td>{modification.name}</Td>
                <Td>{getParameterValue(modification.parameters, 'year')}</Td>
                <Td>{getParameterValue(modification.parameters, 'engine')}</Td>
                <Td>{getParameterValue(modification.parameters, 'trans_type')}</Td>
                <Td>
                  {getParameterValue(modification.parameters, 'sales_region')},{' '}
                  {getParameterValue(modification.parameters, 'steering')}
                </Td>
                <Td>{modification.description}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {!loading && modifications.length > 0 && hasMore && (
        <Box mt={4} textAlign="center">
          <Button onClick={handleLoadMore} colorScheme="blue">
            Показать еще 20
          </Button>
        </Box>
      )}

      {!loading && modifications.length > 0 && !hasMore && (
        <Box mt={4} textAlign="center">
          <Text color="gray.500">Больше модификаций нет</Text>
        </Box>
      )}
    </Box>
  );
};
